import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import { AgentsService } from 'src/app/services/agents/agents.service';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import fakerbr from 'faker-br';
import Utils from 'src/app/helpers/utils';
import { AuthService } from 'src/app/services/auth/auth.service';
import { Router } from '@angular/router';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';

@Component({
  selector: 'app-dashboard-agents',
  templateUrl: './dashboard-agents.component.html',
  styleUrls: ['./dashboard-agents.component.scss'],
})
export class DashboardAgentsComponent implements OnInit {
  agentsForm = this.formBuilder.group({
    name: [null, [Validators.required]],
  });

  permissionObj: PermissionValues = {
    canAdd: false,
    canChange: false,
    canRemove: false,
  };

  addAgents = false;

  agents: any[] = [];
  agent: any = undefined;

  filteredData = this.agents;

  loading = false;
  savingAgent = false;
  deletingAgent = false;

  // Carbon Warning Modal properties
  showDeleteModal = false;
  agentToDelete: any = null;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    private agentsService: AgentsService,
    private signupService: SignupService,
    private authService: AuthService,
    private router: Router,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.authService.canViewPage(['de_can_view_agents']);

    this.setBreadcrumb();
    this.getRoles();
    this.setData();
    this.setPermissions();
  }

  async getRoles() {
    this.loading = true;

    try {
      const res = await this.api.get({
        route: 'api/registration/agent/',
        token: true,
      });

      this.agents = res
        .map((item: any) => {
          return {
            ...item,
            label: item.name,
            value: item.id,
          };
        })
        .reverse();

      this.filteredData = this.agents;

      this.agentsService.data = this.agents;
      this.agentsService.onChange.emit();
    } catch (error) {
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }

    this.loading = false;
  }

  addAgentsToggle(status: boolean) {
    this.addAgents = status;

    if (!status) this.agent = undefined;
  }

  async addAgent() {
    this.savingAgent = true;
    try {
      const values = this.agentsForm.value;

      const payload = {
        name: values.name,
      };

      const alreadyHaveAgent = this.agents.find(
        (_agent) =>
          _agent.name === payload.name &&
          (!this.agent || _agent.id !== this.agent.value)
      );

      if (alreadyHaveAgent) {
        this.toast.show('error', 'Erro', 'Já existe um agente com esse nome.');
        this.savingAgent = false;
        return;
      }

      const res = this.agent
        ? await this.api.put({
            route: `api/registration/agent/${this.agent.value}/`,
            token: true,
            body: payload,
          })
        : await this.api.post({
            route: 'api/registration/agent/',
            token: true,
            body: payload,
          });

      this.toast.show(
        'info',
        'Sucesso',
        this.agent
          ? 'Agente atualizado com sucesso.'
          : 'Agente cadastrado com sucesso.'
      );

      this.addAgentsToggle(false);

      this.getRoles();

      this.agent = undefined;
      this.agentsForm.reset();
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao cadastrar o agente.'
      );
    } finally {
      this.savingAgent = false;
    }
  }

  setData() {
    this.signupService.fillFormEvent.subscribe(() => {
      this.agentsForm.patchValue({
        name: fakerbr.name.findName(),
        slug: Utils.convertToSlug(fakerbr.name.findName()),
        description: fakerbr.random.words(3),
        applicable: 'admin',
        is_visible: true,
      });
    });
  }

  setAgent(agent: any) {
    this.agentsForm.patchValue({
      name: agent.label,
    });
    this.agent = agent;
    this.addAgentsToggle(true);
  }

  openDeleteModal(agent: any) {
    this.agentToDelete = agent;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.agentToDelete = null;
  }

  async confirmDeleteAgent() {
    if (!this.agentToDelete) return;

    this.deletingAgent = true;

    try {
      await this.api.delete({
        route: `api/registration/agent/${this.agentToDelete.id}/`,
        token: true,
      });

      this.toast.show('info', 'Sucesso', 'Agente removido com sucesso!');

      this.closeDeleteModal();
      await this.getRoles();
    } catch (error: any) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.detail || 'Ocorreu um erro ao apagar o agente.'
      );
      this.closeDeleteModal();
    } finally {
      this.deletingAgent = false;
    }
  }

  // Modal methods
  getDeleteModalTitle() {
    return 'Tem certeza?';
  }

  getDeleteModalMessage() {
    return 'Você não poderá reverter isso!';
  }

  getDeleteConfirmText() {
    return 'Sim, apague!';
  }

  getDeleteCancelText() {
    return 'Cancelar';
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_agents`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  setBreadcrumb(): void {
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Configurações',
        url: '/app/settings',
        clickable: true,
      },
      {
        label: 'Agentes',
        url: '/app/settings/agents',
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }
}
